import {LexicalComposer} from "@lexical/react/LexicalComposer";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";

import {RichTextPlugin} from "@lexical/react/LexicalRichTextPlugin";
import {ContentEditable} from "@lexical/react/LexicalContentEditable";
import {HistoryPlugin} from "@lexical/react/LexicalHistoryPlugin";
import {AutoFocusPlugin} from "@lexical/react/LexicalAutoFocusPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import {CharacterLimitPlugin} from '@lexical/react/LexicalCharacterLimitPlugin';
import {TabIndentationPlugin} from '@lexical/react/LexicalTabIndentationPlugin';

import TreeViewPlugin from "./plugins/TreeViewPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import {HeadingNode, QuoteNode} from "@lexical/rich-text";
import {TableCellNode, TableNode, TableRowNode} from "@lexical/table";
import {ListItemNode, ListNode} from "@lexical/list";
import {CodeHighlightNode, CodeNode} from "@lexical/code";
import {AutoLinkNode, LinkNode} from "@lexical/link";
import {LinkPlugin} from "@lexical/react/LexicalLinkPlugin";
import {ListPlugin} from "@lexical/react/LexicalListPlugin";
import {MarkdownShortcutPlugin} from "@lexical/react/LexicalMarkdownShortcutPlugin";
import {TRANSFORMERS} from "@lexical/markdown";
import {OnChangePlugin} from "@lexical/react/LexicalOnChangePlugin";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import AutoLinkPlugin from "./plugins/AutoLinkPlugin";
import {$generateHtmlFromNodes} from "@lexical/html";
import {OverflowNode} from '@lexical/overflow';

import React, {useEffect, useMemo, useState} from "react";
import ExampleTheme from "./themes/ExampleTheme";
import {$generateNodesFromDOM} from "@lexical/html/LexicalHtml";
import {$getRoot, $insertNodes} from "lexical";
import {useMount, useUnmount} from "react-use";
import {MaxLengthPlugin} from "./plugins/MaxLengthPlugin";
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import {NodeEventPlugin} from "@lexical/react/LexicalNodeEventPlugin";

const Placeholder = ({placeholder = 'Enter text here...'}) => {
    return <div className="editor-placeholder">{placeholder}</div>;
}

const EMPTY_CONTENT =
    '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

const HandleChangeListener = ({initValue = '', initialized, onInit, onChange, isDisabled}) => {
    const [editor] = useLexicalComposerContext();

    const [editable, setEditable] = useState(false);

    useMount(() => {
        if (initValue?.replace(/(<(?!\/)[^>]+>)+(<\/[^>]+>)+/, '')?.length) {
            editor.update(async () => {
                // In the browser you can use the native DOMParser API to parse the HTML string.
                const parser = new DOMParser();
                const dom = parser.parseFromString(initValue, 'text/html');

                // Once you have the DOM instance it's easy to generate LexicalNodes.
                const nodes = $generateNodesFromDOM(editor, dom);

                // Select the root
                $getRoot().select();

                // Insert them at a selection.
                $insertNodes(nodes);
                if (!initialized && editor.getRootElement()) {
                    onInit(true)
                }
            });
        }else {
            setTimeout(() => {
                if (!initialized && editor.getRootElement()) {
                    onInit(true)
                }
            }, 600)
        }
    })


    useEffect(() => {
        if (!isDisabled !== editable) {
            setEditable(!isDisabled);
            editor.setEditable(!isDisabled);
        }
    }, [isDisabled]);


    const onEditorChange = (editorState) => {
        editorState.read(() => {
            // Read the contents of the EditorState here.
            const htmlString = $generateHtmlFromNodes(editor, null);

            return onChange(htmlString)
        });
    };



    return <OnChangePlugin onChange={onEditorChange}/>;
};

const Editor = ({
                    maxLength = 10000, isReadOnly = false, hideToolbar = false,
                    enableScroll = false, value = '',
                    placeholder = 'Enter text here...', disabled = false,
                    onChange = () => {}, ...props
                }) => {

    const [hasInitialized, setHasInitialized] = useState(false);

    const editorConfig = useMemo(() => {
        let nodes = [
            HeadingNode,
            ListNode,
            ListItemNode,
            QuoteNode,
            CodeNode,
            CodeHighlightNode,
            TableNode,
            TableCellNode,
            TableRowNode,
            AutoLinkNode,
            LinkNode,
            OverflowNode
        ];

        if (hideToolbar) {
            nodes = [OverflowNode];
        }

        return {
            // The editor theme
            theme: ExampleTheme,
            editable: false,
            // Handling of errors during update
            onError(error) {
                throw error;
            },
            // Any custom nodes go here
            nodes
        };
    }, [hideToolbar]);

    return (
        <div className={TB.mapClass("lexical-wripple relative", {
            "editor-disabled": isReadOnly || disabled,
            "pointer-events-none": !hasInitialized
        })}>
            <LexicalComposer initialConfig={editorConfig}>
                <div className="editor-container">
                    {!hideToolbar && <ToolbarPlugin isDisabled={isReadOnly || disabled} tools={props.tools} extra={props.extra}/>}
                    <div className="editor-inner">
                        <RichTextPlugin
                            contentEditable={<ContentEditable className="editor-input"/>}
                            placeholder={<Placeholder placeholder={placeholder}/>}
                            ErrorBoundary={LexicalErrorBoundary}
                        />
                        {!hideToolbar && (
                            <React.Fragment>
                                <HistoryPlugin/>
                                {!props.disableAutoFocus && <AutoFocusPlugin/>}
                                <CodeHighlightPlugin/>
                                <ListPlugin/>
                                <LinkPlugin/>
                                <AutoLinkPlugin/>
                                <TabIndentationPlugin/>
                                <ListMaxIndentLevelPlugin maxDepth={7}/>
                                <MarkdownShortcutPlugin transformers={TRANSFORMERS}/>
                            </React.Fragment>
                        )}
                    </div>
                    <MaxLengthPlugin maxLength={maxLength}/>
                </div>

                <HandleChangeListener onInit={async () => {
                    console.log('inittttt', true)
                    if (props.disableAutoFocus) {
                        await TB.wait(150);
                        console.log('disable auto focus', props.disableAutoFocus)
                    }
                    await setHasInitialized(true)

                }}
                                      initialized={hasInitialized}
                                      isDisabled={!hasInitialized || isReadOnly || disabled}
                                      onChange={onChange} initValue={value}/>
            </LexicalComposer>
        </div>
    );
}
export default Editor;